import { useRef, useState } from 'react'
import { RotateCcw, Trash2, GripVertical } from 'lucide-react'
import clsx from 'clsx'
import ConfirmDialog from '../ui/ConfirmDialog.jsx'

/** Drag-to-reorder page rail with delete + rotate — the primary page
 *  management surface (replaces the old link-editor-only thumbnail rail). */
export default function PageManager({ pages, onReorder, onDelete, onRotate, busy }) {
  const [dragIdx, setDragIdx] = useState(null)
  const [overIdx, setOverIdx] = useState(null)
  const [confirmIdx, setConfirmIdx] = useState(null)
  const dragging = useRef(false)

  function handleDrop(idx) {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setOverIdx(null); return }
    onReorder(dragIdx, idx)
    setDragIdx(null); setOverIdx(null)
  }

  if (!pages.length) return null

  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {pages.map((p, i) => (
        <div
          key={p.remoteKey || p.url || i}
          draggable
          onDragStart={() => { setDragIdx(i); dragging.current = true }}
          onDragEnd={() => { setDragIdx(null); setOverIdx(null); dragging.current = false }}
          onDragOver={(e) => { e.preventDefault(); setOverIdx(i) }}
          onDrop={() => handleDrop(i)}
          className={clsx(
            'relative shrink-0 w-[92px] rounded-md overflow-hidden border transition-all duration-150 cursor-grab active:cursor-grabbing group',
            overIdx === i && dragIdx !== i ? 'border-blue ring-2 ring-blue/40' : 'border-line',
            dragIdx === i && 'opacity-40'
          )}
        >
          <img src={p.thumbUrl} alt={`Page ${i + 1}`} className="w-full aspect-[3/4] object-cover bg-panel2" />
          <div className="absolute top-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded-full">{i + 1}</div>
          <GripVertical size={12} className="absolute top-1 right-1 text-white/70" />

          <div className="absolute inset-x-0 bottom-0 flex opacity-0 group-hover:opacity-100 transition-opacity duration-150 bg-gradient-to-t from-black/80 to-transparent pt-4">
            <button
              disabled={busy}
              onClick={() => onRotate(i)}
              className="flex-1 py-1.5 text-white hover:text-sky transition-colors disabled:opacity-40"
              title="Rotate 90°"
            >
              <RotateCcw size={13} className="mx-auto" />
            </button>
            <button
              disabled={busy}
              onClick={() => setConfirmIdx(i)}
              className="flex-1 py-1.5 text-white hover:text-danger transition-colors disabled:opacity-40"
              title="Delete page"
            >
              <Trash2 size={13} className="mx-auto" />
            </button>
          </div>
        </div>
      ))}

      <ConfirmDialog
        open={confirmIdx !== null}
        onClose={() => setConfirmIdx(null)}
        onConfirm={() => onDelete(confirmIdx)}
        title="Delete this page?"
        description={confirmIdx !== null ? `Page ${confirmIdx + 1} will be permanently removed from this flipbook.` : ''}
      />
    </div>
  )
}
